import { useState, useEffect, useCallback } from 'react';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { DatePicker } from '@/components/ui/date-picker';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Separator } from '@/components/ui/separator';
import { 
  Building2, 
  Mail, 
  Phone, 
  Globe, 
  MapPin, 
  CreditCard, 
  Hash, 
  Calendar,
  Save,
  RotateCcw,
  AlertCircle,
  CheckCircle2,
  Info
} from 'lucide-react';
import api from '@/lib/api';
import { Firm } from '@/types/settings';
import { formatDate } from '@/lib/dates';

interface FirmInfoSettingsProps {
  firm: Firm;
  canModify: boolean;
  onFirmUpdate: () => void;
}

interface FormData {
  // Basic Information
  name: string;
  email: string;
  phone: string;
  website: string;
  address: string;
  country: string;
  // Financial Information
  vat_number: string;
  bank_account: string;
  invoice_accounting_bcc: string;
  // Financial Year Configuration
  financial_year_start_date: string; // YYYY-MM-DD format
  // Invoice Configuration
  invoice_year: number;
  invoice_number: number;
  invoice_number_format: string;
  // Credit Note Configuration
  credit_note_number: number;
  credit_note_number_format: string;
  // Service Charge
  default_service_charge_rate: string;
  // Invoice Defaults
  default_invoice_detail_level: 'short' | 'summary' | 'detailed';
}

// Get the default financial year start date (January 1st of current year)
const getDefaultFinancialYearStartDate = (): string => {
  const now = new Date();
  return `${now.getFullYear()}-01-01`;
};

// Format date for display
const formatDateForDisplay = (dateString: string | undefined): string => {
  if (!dateString) return 'Not set';
  return formatDate(dateString) || dateString;
};

// Calculate the next financial year start date
const getNextFinancialYearDate = (currentDate: string): string => {
  if (!currentDate) return '';
  try {
    const date = new Date(currentDate);
    date.setFullYear(date.getFullYear() + 1);
    return date.toISOString().split('T')[0];
  } catch {
    return '';
  }
};

export function FirmInfoSettings({ firm, canModify, onFirmUpdate }: FirmInfoSettingsProps) {
  const getInitialFormData = useCallback((): FormData => ({
    name: firm.name,
    email: firm.email,
    phone: firm.phone,
    website: firm.website || '',
    address: firm.address,
    country: firm.country,
    vat_number: firm.vat_number || '',
    bank_account: firm.bank_account || '',
    invoice_accounting_bcc: firm.invoice_accounting_bcc || '',
    financial_year_start_date: firm.financial_year_start_date || getDefaultFinancialYearStartDate(),
    invoice_year: firm.invoice_year,
    invoice_number: firm.invoice_number,
    invoice_number_format: firm.invoice_number_format || '{year:04d}{number:05d}',
    credit_note_number: firm.credit_note_number || 1,
    credit_note_number_format: firm.credit_note_number_format || 'CN{year:04d}{number:05d}',
    default_service_charge_rate: firm.default_service_charge_rate || '6.00',
    default_invoice_detail_level: firm.default_invoice_detail_level || 'detailed',
  }), [firm]);

  const [formData, setFormData] = useState<FormData>(getInitialFormData());
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Reset form when firm changes
  useEffect(() => {
    setFormData(getInitialFormData());
    setError(null);
    setSuccess(null);
  }, [firm, getInitialFormData]);

  const handleInputChange = (field: keyof FormData, value: string | number) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setSuccess(null);
    setError(null);
  };

  const hasChanges = (): boolean => {
    const initial = getInitialFormData();
    return JSON.stringify(formData) !== JSON.stringify(initial);
  };

  const handleSave = async () => {
    if (!canModify) return;

    try {
      setSaving(true);
      setError(null);
      setSuccess(null);

      const updateData = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        website: formData.website,
        address: formData.address,
        country: formData.country,
        vat_number: formData.vat_number,
        bank_account: formData.bank_account,
        invoice_accounting_bcc: formData.invoice_accounting_bcc,
        financial_year_start_date: formData.financial_year_start_date,
        invoice_year: formData.invoice_year,
        invoice_number: formData.invoice_number,
        invoice_number_format: formData.invoice_number_format,
        credit_note_number: formData.credit_note_number,
        credit_note_number_format: formData.credit_note_number_format,
        default_service_charge_rate: formData.default_service_charge_rate,
        default_invoice_detail_level: formData.default_invoice_detail_level,
      };

      await api.patch(`/api/firms/${firm.id}/`, updateData);
      setSuccess('Firm settings updated successfully');
      onFirmUpdate();
    } catch (err: any) {
      const errorMessage = err.response?.data?.message 
        || err.response?.data?.detail 
        || Object.values(err.response?.data || {}).flat().join(', ')
        || 'Failed to update firm settings';
      setError(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setFormData(getInitialFormData());
    setSuccess(null);
    setError(null);
  };

  const nextFinancialYear = getNextFinancialYearDate(formData.financial_year_start_date);

  return (
    <div className="space-y-6">
      {/* Alerts */}
      {!canModify && (
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            You don't have permission to modify firm settings.
          </AlertDescription>
        </Alert>
      )}

      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {success && (
        <Alert className="border-green-500 bg-green-50 dark:bg-green-950">
          <CheckCircle2 className="h-4 w-4 text-green-600" />
          <AlertDescription className="text-green-800 dark:text-green-200">{success}</AlertDescription>
        </Alert>
      )}

      {/* Basic Information Section */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Building2 className="h-5 w-5 text-primary" />
            Basic Information
          </CardTitle>
          <CardDescription>Your firm's contact details and identification</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="name" className="flex items-center gap-2">
                <Building2 className="h-4 w-4 text-muted-foreground" />
                Firm Name
              </Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                disabled={!canModify}
                placeholder="Your Law Firm Name"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="email" className="flex items-center gap-2">
                <Mail className="h-4 w-4 text-muted-foreground" />
                Email Address
              </Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                disabled={!canModify}
                placeholder="info@yourfirm.com"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4">
            <div className="space-y-2">
              <Label htmlFor="invoice_accounting_bcc" className="flex items-center gap-2">
                <Mail className="h-4 w-4 text-muted-foreground" />
                Invoice Accounting BCC
              </Label>
              <Input
                id="invoice_accounting_bcc"
                type="email"
                value={formData.invoice_accounting_bcc}
                onChange={(e) => handleInputChange('invoice_accounting_bcc', e.target.value)}
                disabled={!canModify}
                placeholder="accounting@yourfirm.com"
              />
              <p className="text-xs text-muted-foreground flex items-start gap-1">
                <Info className="h-3 w-3 mt-0.5 flex-shrink-0" />
                All invoices, credit notes, and down payments will automatically include this email in BCC
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="phone" className="flex items-center gap-2">
                <Phone className="h-4 w-4 text-muted-foreground" />
                Phone Number
              </Label>
              <Input
                id="phone"
                value={formData.phone}
                onChange={(e) => handleInputChange('phone', e.target.value)}
                disabled={!canModify}
                placeholder="+32 123 456 789"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="website" className="flex items-center gap-2">
                <Globe className="h-4 w-4 text-muted-foreground" />
                Website
              </Label>
              <Input
                id="website"
                type="url"
                value={formData.website}
                onChange={(e) => handleInputChange('website', e.target.value)}
                disabled={!canModify}
                placeholder="https://yourfirm.com"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="address" className="flex items-center gap-2">
              <MapPin className="h-4 w-4 text-muted-foreground" />
              Address
            </Label>
            <Input
              id="address"
              value={formData.address}
              onChange={(e) => handleInputChange('address', e.target.value)}
              disabled={!canModify}
              placeholder="123 Legal Street, City, Country"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="country" className="flex items-center gap-2">
                <Globe className="h-4 w-4 text-muted-foreground" />
                Country
              </Label>
              <Input
                id="country"
                value={formData.country}
                onChange={(e) => handleInputChange('country', e.target.value)}
                disabled={!canModify}
                placeholder="Belgium"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="vat_number" className="flex items-center gap-2">
                <CreditCard className="h-4 w-4 text-muted-foreground" />
                VAT Number
              </Label>
              <Input
                id="vat_number"
                value={formData.vat_number}
                onChange={(e) => handleInputChange('vat_number', e.target.value)}
                disabled={!canModify}
                placeholder="BE0123456789"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="bank_account" className="flex items-center gap-2">
              <CreditCard className="h-4 w-4 text-muted-foreground" />
              Bank Account
            </Label>
            <Input
              id="bank_account"
              value={formData.bank_account}
              onChange={(e) => handleInputChange('bank_account', e.target.value)}
              disabled={!canModify}
              placeholder="IBAN: BE89 3704 0044 0532 0130 00"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="default_service_charge_rate" className="flex items-center gap-2">
                <CreditCard className="h-4 w-4 text-muted-foreground" />
                Default Service Charge Rate (%)
              </Label>
              <Input
                id="default_service_charge_rate"
                type="text"
                inputMode="decimal"
                value={formData.default_service_charge_rate}
                onChange={(e) => {
                  const v = e.target.value;
                  if (v === '' || /^\d*\.?\d*$/.test(v)) {
                    handleInputChange('default_service_charge_rate', v);
                  }
                }}
                disabled={!canModify}
                placeholder="6.00"
              />
              <p className="text-xs text-muted-foreground flex items-start gap-1">
                <Info className="h-3 w-3 mt-0.5 flex-shrink-0" />
                Applied to invoices unless overridden per participant in a fee sharing configuration
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="default_invoice_detail_level" className="flex items-center gap-2">
                <Hash className="h-4 w-4 text-muted-foreground" />
                Default Invoice Detail Level
              </Label>
              <Select
                value={formData.default_invoice_detail_level}
                onValueChange={(v) => handleInputChange('default_invoice_detail_level', v)}
                disabled={!canModify}
              >
                <SelectTrigger id="default_invoice_detail_level">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="short">Invoice only</SelectItem>
                  <SelectItem value="summary">Invoice + Summary of timesheets</SelectItem>
                  <SelectItem value="detailed">Invoice + Timesheets</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground flex items-start gap-1">
                <Info className="h-3 w-3 mt-0.5 flex-shrink-0" />
                Controls how much detail is shown on generated invoices. Can be overridden per invoice.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Financial Year Configuration */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Calendar className="h-5 w-5 text-primary" />
            Financial Year Configuration
          </CardTitle>
          <CardDescription>Configure the start date of your current financial year</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="financial_year_start_date" className="flex items-center gap-2">
                <Calendar className="h-4 w-4 text-muted-foreground" />
                Financial Year Start Date
              </Label>
              <DatePicker
                id="financial_year_start_date"
                value={formData.financial_year_start_date}
                onChange={(iso) => handleInputChange('financial_year_start_date', iso)}
                disabled={!canModify}
              />
            </div>

            <div className="space-y-2">
              <Label className="flex items-center gap-2 text-muted-foreground">
                <Calendar className="h-4 w-4" />
                Next Financial Year Starts
              </Label>
              <DatePicker
                value={nextFinancialYear}
                onChange={() => {}}
                disabled
                className="bg-muted"
              />
            </div>
          </div>

          <Alert className="bg-muted/50">
            <Info className="h-4 w-4" />
            <AlertDescription>
              <p>
                <strong>Current financial year:</strong> {formatDateForDisplay(formData.financial_year_start_date)}
              </p>
              <p className="text-sm mt-1">
                This date determines when your financial year started. When the year ends, 
                update this to the next financial year start date (e.g., from {formatDateForDisplay(formData.financial_year_start_date)} to {formatDateForDisplay(nextFinancialYear)}).
              </p>
            </AlertDescription>
          </Alert>
        </CardContent>
      </Card>

      {/* Invoice Numbering Configuration */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Hash className="h-5 w-5 text-primary" />
            Invoice Numbering
          </CardTitle>
          <CardDescription>Configure how invoice numbers are generated</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="invoice_year">Invoice Year</Label>
              <Input
                id="invoice_year"
                type="number"
                value={formData.invoice_year}
                onChange={(e) => handleInputChange('invoice_year', parseInt(e.target.value) || new Date().getFullYear())}
                disabled={!canModify}
                min="2020"
                max="2099"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="invoice_number">Last Invoice Number Used</Label>
              <Input
                id="invoice_number"
                type="number"
                value={formData.invoice_number}
                onChange={(e) => handleInputChange('invoice_number', parseInt(e.target.value) || 1)}
                disabled={!canModify}
                min="1"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="invoice_number_format">Invoice Number Format</Label>
            <Input
              id="invoice_number_format"
              value={formData.invoice_number_format}
              onChange={(e) => handleInputChange('invoice_number_format', e.target.value)}
              disabled={!canModify}
              placeholder="{year:04d}{number:05d}"
            />
            <p className="text-xs text-muted-foreground">
              Use placeholders: <code className="bg-muted px-1 rounded">{'{year}'}</code> for year, <code className="bg-muted px-1 rounded">{'{number}'}</code> for sequential number.
              Example: <code className="bg-muted px-1 rounded">{'{year:04d}{number:05d}'}</code> produces <strong>202500001</strong>
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Credit Note Numbering Configuration */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Hash className="h-5 w-5 text-primary" />
            Credit Note Numbering
          </CardTitle>
          <CardDescription>Configure how credit note numbers are generated</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="credit_note_number">Last Credit Note Number Used</Label>
            <Input
              id="credit_note_number"
              type="number"
              value={formData.credit_note_number}
              onChange={(e) => handleInputChange('credit_note_number', parseInt(e.target.value) || 1)}
              disabled={!canModify}
              min="1"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="credit_note_number_format">Credit Note Number Format</Label>
            <Input
              id="credit_note_number_format"
              value={formData.credit_note_number_format}
              onChange={(e) => handleInputChange('credit_note_number_format', e.target.value)}
              disabled={!canModify}
              placeholder="CN{year:04d}{number:05d}"
            />
            <p className="text-xs text-muted-foreground">
              Use placeholders: <code className="bg-muted px-1 rounded">{'{year}'}</code> for year, <code className="bg-muted px-1 rounded">{'{number}'}</code> for sequential number.
              Example: <code className="bg-muted px-1 rounded">CN{'{year:04d}{number:05d}'}</code> produces <strong>CN202500001</strong>
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Subscription Information (Read-only) */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <CreditCard className="h-5 w-5 text-primary" />
            Sentra Subscription
          </CardTitle>
          <CardDescription>Your subscription details (read-only)</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            <div className="space-y-1">
              <p className="text-sm text-muted-foreground">Contract Start</p>
              <p className="text-lg font-semibold">{firm.contract_start_date || 'Not set'}</p>
            </div>
            <div className="space-y-1">
              <p className="text-sm text-muted-foreground">Contract End</p>
              <p className="text-lg font-semibold">{firm.contract_end_date || 'Not set'}</p>
            </div>
            <div className="space-y-1">
              <p className="text-sm text-muted-foreground">Max Users</p>
              <p className="text-lg font-semibold">{firm.max_users || 'Unlimited'}</p>
            </div>
            <div className="space-y-1">
              <p className="text-sm text-muted-foreground">Current Users</p>
              <p className="text-lg font-semibold">{firm.user_count || 0}</p>
            </div>
          </div>
          
          {firm.subscription_price && (
            <>
              <Separator className="my-4" />
              <div className="space-y-1">
                <p className="text-sm text-muted-foreground">Subscription Price</p>
                <p className="text-lg font-semibold">€{firm.subscription_price}</p>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Action Buttons */}
      {canModify && (
        <div className="flex justify-end gap-3 pt-4">
          <Button 
            variant="outline" 
            onClick={handleReset} 
            disabled={saving || !hasChanges()}
            className="gap-2"
          >
            <RotateCcw className="h-4 w-4" />
            Reset Changes
          </Button>
          <Button 
            onClick={handleSave} 
            disabled={saving || !hasChanges()}
            className="gap-2"
          >
            <Save className="h-4 w-4" />
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>
      )}
    </div>
  );
}
